import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

// Define allowed routes for CUSTOMER role
const CUSTOMER_ALLOWED_ROUTES = [
  '/inventory/products',
  '/inventory/stock',
  '/inventory/outbound',  // Read-only access, requires itemSku filter (enforced in OutboundOrderPage)
  '/login',
];

// Check if current path is allowed for CUSTOMER role
const isCustomerAllowedRoute = (pathname: string): boolean => {
  return CUSTOMER_ALLOWED_ROUTES.some(route =>
    pathname === route || pathname.startsWith(route + '/')
  );
};

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
    const { t } = useTranslation();
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading,
    user,
    loadUserProfile,
    logout
  } = useAuthStore();

  // 初始化時載入用戶資料
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && !user && !isLoading) {
      loadUserProfile();
    }
  }, [user, isLoading, loadUserProfile]);

  // 如果沒有 token，確保重置認證狀態，避免重複跳轉
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  useEffect(() => {
    if (!token && isAuthenticated) {
      logout();
    }
  }, [token, isAuthenticated, logout]);

  // 正在載入中
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" tip={t('載入中...')} />
      </div>
    );
  }

  // 未認證，重定向到登錄頁
  if (!isAuthenticated || !token) {
    const base = ((import.meta as any)?.env?.BASE_URL) || '/';
    const to = (base?.endsWith('/') ? base : `${base}/`) + 'login';
    return <Navigate to={to} state={{ from: location }} replace />;
  }

  // 檢查角色權限
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'ADMIN') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h3>{t('權限不足')}</h3>
        <p>{t('您沒有權限訪問此頁面')}</p>
      </div>
    );
  }

  // 如果是 PICK 角色且當前不在 PICK 專屬路由中，則重定向到 PICK 專屬頁面
  if (user?.role === 'PICK_SCANNING' && !location.pathname.startsWith('/pick')) {
    return <Navigate to="/pick/inventory/picking" state={{ from: location }} replace/>;
  }

  // 如果是 PACK 角色且當前不在 PACK 專屬路由中，則重定向到 PACK 專屬頁面
  if (user?.role === 'PACK_SCANNING' && !location.pathname.startsWith('/pack')) {
    return <Navigate to="/pack/inventory/packing" state={{ from: location }} replace/>;
  }
  if (user?.role === 'PICKUP_SCANNING' && !location.pathname.startsWith('/pickup')) {
    return <Navigate to="/pickup/inventory/packing" state={{ from: location }} replace/>;
  }

  // 如果是 INBOUND_OPERATOR 角色且當前不在 INBOUND 專屬路由中，則重定向到 INBOUND 專屬頁面
  if (user?.role === 'INBOUND_OPERATOR' && !location.pathname.startsWith('/inbound')) {
    return <Navigate to="/inbound/inventory/inbound" state={{ from: location }} replace/>;
  }

  // 如果是 CUSTOMER 角色，只允許訪問指定的路由
  if (user?.role === 'CUSTOMER') {
    // If accessing homepage, redirect to products page
    if (location.pathname === '/') {
      return <Navigate to="/inventory/products" state={{ from: location }} replace />;
    }
    // If accessing a non-allowed route, show permission denied
    if (!isCustomerAllowedRoute(location.pathname)) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column'
        }}>
          <h3>{t('權限不足')}</h3>
          <p>{t('您沒有權限訪問此頁面')}</p>
          <p style={{ color: '#999', fontSize: '12px' }}>{t('Access denied for Customer role')}</p>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;